import {
  DatePicker,
  Flex,
  Text,
  TextInput,
  TextInputProps,
  createForm,
  useToasts,
} from '@applyboard/crystal-ui'
import { IAdditionalDocumentDialog, IPassportDocumentDialogForm } from '../types'
import { documentTypeOptions } from '../../../../utils'
import { DocumentTags } from '../../../../utils/enums'
import { FileUploadField } from '../../ApplicationForms/FileUploadField'
import { DocumentDialog } from '../DocumentDialog'
import { useUpdateRequestedDocuments } from '../../../../hooks'
import { ComponentType, useEffect, useRef, useState } from 'react'
import { nanoid } from 'nanoid'
import { useApplicationFormContext } from '../../ApplicationForms/ApplicationForm'
import { Asterisk } from '../../../Asterisk'
import { useUpdatePassport } from '../../../../hooks/useUpdatePassport'
import { useUploadPassport } from '../../../../hooks/useUploadPassport'
import { transformFileInfoToUploadFileData } from '../../../../utils/transformFileInfoToUploadFileData'
import { DocumentType } from '@applyboard/school-applications-types-lib'

type PassportRequestFormFields = {
  passportNumber: string
  passportExpiryDate: string
  passportFiles: Array<{
    id: string
    file: File
  }>
}

type UpdatePassportProps = {
  id?: string
  passportNumber?: string
  passportExpiryDate?: string
}

const { Form, Field, useFieldValues, useSetFieldValues } = createForm<PassportRequestFormFields>()

const UPLOAD_LIMIT = 1

export function PassportDialog(props: IAdditionalDocumentDialog) {
  const { pendingFileUploadState, resetFiles } = useApplicationFormContext()
  const [open, setOpen] = useState(false)
  const toast = useToasts()
  const { isUpdatingApplication, updateRequestedDocuments } = useUpdateRequestedDocuments({
    applicationId: props.application.id,
    index: props.index,
  })
  const { isUploadingPassport, uploadPassport } = useUploadPassport()
  const [updatePassportProps, setUpdatePassportProps] = useState<UpdatePassportProps>()
  const { isUpdatingPassport, updatePassport } = useUpdatePassport({
    id: updatePassportProps?.id,
  })

  const passportId = useRef<string>()
  useEffect(() => {
    passportId.current = nanoid()
  }, [])

  const requestedDocuments = props.application.attributes?.requestedDocuments?.[props.id]

  useEffect(() => {
    if (
      requestedDocuments &&
      updatePassportProps?.id &&
      updatePassportProps?.passportNumber &&
      updatePassportProps.passportExpiryDate
    ) {
      const updatePassportPayload = {
        passportNumber: updatePassportProps.passportNumber,
        expiryDate: updatePassportProps.passportExpiryDate,
        nationality:
          props.application.attributes?.personalInformation?.basicPersonalInformation?.nationality,
      }
      updatePassport(updatePassportPayload, {
        onSuccess: response => {
          if (pendingFileUploadState) {
            updateRequestedDocuments({
              additionalData: {
                statusAndCitizenship: {
                  passports: {
                    [passportId.current as string]: {
                      passportId: updatePassportProps.id,
                    },
                  },
                },
              },
              dataFiles: pendingFileUploadState,
              documentType: DocumentTags.PASSPORT,
              onSuccess: () => {
                setOpen(false)
              },
              requestedDocuments,
              requestedDocumentsId: props.id,
              sectionReference: passportId.current,
            })
          }
        },
      })
    }
  }, [updatePassportProps])

  const uploadRequestedPassport = (data: PassportRequestFormFields) => {
    const passportFile = data.passportFiles[0]
    const passportDocument = transformFileInfoToUploadFileData(passportFile, {
      sectionReference: `${passportId.current}`,
      type: DocumentTags.PASSPORT as unknown as DocumentType,
    })
    uploadPassport(passportDocument, {
      onSuccess: response => {
        setUpdatePassportProps({
          id: response.data.id,
          passportNumber: data.passportNumber,
          passportExpiryDate: data.passportExpiryDate,
        })
      },
      onError: err => {
        toast.negative(new Error(err.message))
      },
    })
  }

  if (!requestedDocuments) {
    return null
  }

  return (
    <DocumentDialog
      document={props.document}
      heading={`Add ${documentTypeOptions[props.document.documentType].label}`}
      loading={isUpdatingApplication || isUpdatingPassport || isUploadingPassport}
      open={open}
      setOpen={open => {
        resetFiles({})
        setOpen(open)
      }}
      form={`passport-document-request-${passportId.current}`}
    >
      <Flex direction="column" gap={4}>
        <Text contrast="mid">{props.document.note}</Text>
        <Form
          id={`passport-document-request-${passportId.current}`}
          defaultValues={{
            passportNumber: '',
            passportExpiryDate: '',
            passportFiles: [],
          }}
          onSubmit={data => uploadRequestedPassport(data)}
        >
          <PassportDialogForm
            passportId={passportId.current as string}
            application={props.application}
            document={props.document}
          />
        </Form>
      </Flex>
    </DocumentDialog>
  )
}

function PassportDialogForm(props: IPassportDocumentDialogForm) {
  const { getObservableFiles } = useApplicationFormContext()
  const { passportFiles } = useFieldValues(['passportFiles'])
  const setFieldValues = useSetFieldValues()
  const maxDate = new Date()
  const minDate = new Date()
  maxDate.setFullYear(maxDate.getFullYear() + 10)
  minDate.setFullYear(minDate.getFullYear() - 3)

  return (
    <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
      <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
        <Field
          as={TextInput as ComponentType<TextInputProps>}
          label="Passport number"
          name="passportNumber"
          required="Passport number is required"
        />
      </Flex.Item>
      <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
        <Field
          as={DatePicker}
          label="Passport expiry date"
          name="passportExpiryDate"
          maxDate={maxDate.toISOString()}
          minDate={minDate.toISOString()}
          required="Passport expiry date is required"
        />
      </Flex.Item>
      <Flex gap={4} direction="column" basis="100%">
        <Field
          as={FileUploadField}
          allowedFileTypes={['.jpg', '.pdf', '.png', '.jpeg']}
          application={props.application}
          fileLimit={UPLOAD_LIMIT}
          fileType={DocumentTags.PASSPORT}
          label={
            <Text variant="bodyS" contrast="mid">
              Add your passport document below, supported file formats: JPG, JPEG, PDF, PNG, max
              number of files: {UPLOAD_LIMIT} <Asterisk />
            </Text>
          }
          name={`passportFiles`}
          multiple={false}
          onRemove={(id: string) =>
            setFieldValues({ passportFiles: passportFiles.filter(file => file.id !== id) })
          }
          section={props.passportId}
          showHistory={false}
          validate={value => {
            const observableFiles = getObservableFiles({
              fileType: DocumentTags.PASSPORT,
              sectionReference: props.passportId,
            })

            if (Object.keys(observableFiles).length > UPLOAD_LIMIT) {
              return `This field has a file limit of ${UPLOAD_LIMIT}.`
            }

            if (!(Object.keys(observableFiles).length || value.length)) {
              return 'This field is required'
            }
            return true
          }}
        />
      </Flex>
    </Flex>
  )
}
